import { CurrencyPipe } from '@angular/common';
import { ViewEncapsulation, Component, Input, ElementRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { ConfirmEnableWarrantyComponent } from '../../dialogs/confirm-enable-warranty/confirm-enable-warranty.component';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { RiskModel } from '@toro/common/shared/enum/risk-model.enum';
import { FinanceActions } from '@toro/common/shared/state-management/finance/finance.action';
import { UserProfileActions } from '@toro/common/shared/state-management/userprofile/userprofile.actions';
import { Subscription } from 'rxjs/Subscription';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

@AutoUnsubscribe()
@Component({
    selector: 'tr-warranty-usage',
    templateUrl: './tr-warranty-usage.component.html',
    styleUrls: [ './tr-warranty-usage.component.scss' ],
    encapsulation: ViewEncapsulation.None,
})
/**
 * Componente responsável por exibir e modificar o
 * limite disponível para investimento (usar
 * também a garantia ). Ao solicitar o aumento um contrato é exibido, uma vez aceito
 * o limite é incrementado segundo recursos disponíveis.
 */
export class TrWarrantyUsageComponent implements OnDestroy {

    @Output() public valueChanges: EventEmitter<boolean> = new EventEmitter();

    /** Modelo de garantia atual do usuário */
    public userRiskModel: RiskModel;

    /** Modelos de garantia */
    public riskModelOptions = RiskModel;

    /** Tooltip descritiva do valor disponível para investir */
    public avaliableValueTooltipDescriptor: any;

    public tooltipTemplate: string = '';

    private warrantyBovespaBuyPositionPrivate: boolean = false;

    @Input() set warrantyBovespaBuyPosition(value: boolean) {
        this.warrantyBovespaBuyPositionPrivate = value;
        this.tooltipTemplate = this.getTooltipContent();
    }
    get warrantyBovespaBuyPosition(): boolean {
        return this.warrantyBovespaBuyPositionPrivate;
    }

    /** Limite alterado para o módulo 2 */
    get limitIncreased(): boolean {
        return this.userRiskModel === RiskModel.Two;
    }

    /** Valor disponível para investimento */
    get avaliableValue(): number {
        if (!this.limitIncreased || this.warrantyBovespaBuyPosition) {
            return this.consolidatedPoisitionPrivate.accountBalance || 0;
        }
        return this.avaliableValuePrivate;
    }

    private avaliableValuePrivate: number;

    /** Posição consolidada */
    private consolidatedPoisitionPrivate: any = {};

    private consolidatedPositionSubscription: Subscription;
    private userProfileSubscription: Subscription;

    constructor(
        private dialogsService: DialogsService,
        private utilitiesService: UtilitiesService,
        private element: ElementRef,
        private store: Store<AppState>,
        private currencyPipe: CurrencyPipe,
    ) {
        this.consolidatedPositionSubscription = this.store.select((state) => state.finance.consolidatePosition)
            .subscribe((position) => {
                this.avaliableValuePrivate = position ? position.totalOperationGuarantee : 0;
                this.consolidatedPoisitionPrivate = position || {};
            });

        this.userProfileSubscription = this.store.select((state) => state.userProfile)
            .subscribe((userState) => {
                this.userRiskModel = userState.riskModel;
                this.tooltipTemplate = this.getTooltipContent();
                this.valueChanges.emit(this.limitIncreased);
            });

        this.store.dispatch({type: FinanceActions.getRealtimeConsolidatedPosition});
        this.store.dispatch({type: UserProfileActions.getRiskModel});
    }

    /** Confirma por meio de modal o uso de produtos como garantia para investimentos */
    public confirmIncreaseLimit() {
        this.dialogsService.showComponnent(ConfirmEnableWarrantyComponent, null)
            .afterClosed()
            .subscribe( (result) => {
                if (result) {
                    this.store.dispatch({type: FinanceActions.getRealtimeConsolidatedPosition});
                }
            });
    }

    // tslint:disable-next-line: no-empty
    public ngOnDestroy() { }

    /** Retorna o valor da tooltip om base no uso ou não da garantia */
    public getTooltipContent() {

        if (!this.limitIncreased) {
            return `<div>         
                <div class='pop-title'>Disponível para investir</div>
                    <div class='pop-content'>
                    <table>
                        <tbody>
                            <tr>
                                <td><strong class='font-gray-color--darker'><span class='pull-left'>Conta corrente: &nbsp;</span></strong></td>
                                <td class='text-right'>
                                    <span class='pull-right'>
                                        ${this.currencyPipe.transform(this.consolidatedPoisitionPrivate.accountBalance, 'BRL', 'symbol')}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td><strong class='font-gray-color--darker'><span class='pull-left'>Disponível: </span></strong></td>
                                <td class='text-right'>
                                    <span class='pull-right'>
                                        ${this.currencyPipe.transform(this.consolidatedPoisitionPrivate.accountBalance, 'BRL', 'symbol')}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>`;
        }

        return `<div>         
            <div class='pop-title'>Disponível para investir</div>
            <div class='pop-content'>
                <div class='row'>
                    <table class='col-md-24 col-sm-24 col-xs-24'>
                        <tbody>
                            <tr>
                                <td><strong class='font-gray-color--darker'>Conta corrente:</strong></td>
                                <td class='text-right'>
                                    <span class='pull-right'>
                                        ${this.currencyPipe.transform(this.consolidatedPoisitionPrivate.accountBalance, 'BRL', 'symbol')}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td><strong class='font-gray-color--darker'>Renda Fixa</strong></td>
                                <td class='text-right'>
                                    <span class='pull-right'>
                                        ${this.currencyPipe.transform(this.consolidatedPoisitionPrivate.totalFixedIncome, 'BRL', 'symbol')}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td><strong class='font-gray-color--darker'>Bolsa</strong></td>
                                <td class='text-right'>
                                    <span class='pull-right'>
                                        ${this.currencyPipe.transform(this.consolidatedPoisitionPrivate.totalExchange, 'BRL', 'symbol')}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td><strong class='font-gray-color--darker'>Disponível</strong></td>
                                <td class='text-right'>
                                    <span class='pull-right'>
                                        ${this.currencyPipe.transform(this.avaliableValue, 'BRL', 'symbol')}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p class='font-xsmall--regular limit-tooltip-bottom-wraning'>
                    Seus investimentos em Bolsa e Renda Fixa não somam 100% do seu valor para o cálculo do limite disponível.
                </p>
            </div>
        </div>`;
    }

}
